import * as d3 from 'd3'
import Chart from '../chart'

import './BubbleChart.css'

export default class BubbleChart extends Chart {
  // First step of the D3 rendering.
  create() {
    this.svg = super.createRoot();
    this.main = this.svg.append('g')
      .attr('class', 'main');

    this.axis = this.main.append('g')
      .attr('class', 'axis')
      .attr('transform', `translate(0,0)`);

    this.bubbleLayer = this.main.append('g')
      .attr('class', 'bubbles');

    if(d3.select('.bubbleTooltip').empty()){
      this.tooltip = 
        d3.select('body')
        .append('div')
        .attr('class', 'bubbleTooltip')
        .style('opacity', 0);
    } else {
      this.tooltip = d3.select('.bubbleTooltip')
        .style('opacity', 0);
    }

    const self = this;
    this.zoom = d3.zoom()
      .scaleExtent([1, 8])
      .on('zoom', () => this.zooming(self));
    this.main.call(this.zoom);

    this.bubbles = null;
    this.forceStrength = 0.07;
    this.chargePow = 2.05;
  }

  zooming(self) {
    self.main.attr('transform', d3.event.transform);
  }

  resetZoom() {
    this.main.transition()
      .duration(2500)
      .call( this.zoom.transform, d3.zoomIdentity.translate(0,0).scale(1) );
  }

  zoomToBrussels() {
    this.simulation.stop()

    const brusselsBubble = d3.select('.bubble-Brussels');
    const brusselsCircle = d3.select('.Brussels');

    const radius = brusselsCircle.attr('r')
    const coordinates = brusselsBubble.attr('transform') ?
    // eslint-disable-next-line 
      brusselsBubble.attr('transform').match(/(-?[0-9\.]+)/g).map(c => +c) :
      [this.props.width / 2, this.props.height / 2]
    
    const scale = Math.max(1, Math.min(8, 0.9 / Math.max( 2 * radius / this.props.width, 2 * radius / this.props.height)))
    const translate = [this.props.width / 2 - scale * coordinates[0], this.props.height / 2 - scale * coordinates[1]];
  
    this.main.transition()
        .duration(2500)
        .call( this.zoom.transform, d3.zoomIdentity.translate(translate[0],translate[1]).scale(scale) );
  }

  // Main D3 rendering, that should be redone when the data updates.
  update(state) {
    if (JSON.stringify(state) !== JSON.stringify(this.state)) {
      this.centered = state.centered;
      this.zoomed = state.zoomed;
      this.intl = state.intl;
      this.drawBubble(state);
      this.drawAxis(state);
      if (state.zoomed) {
        this.zoomToBrussels()
      } else {
        this.resetZoom()
      }
      this.state = state;
    }
  }

  drawBubble(state) {
    const self = this;
    const { data, centered } = state;
    const center = { x: this.props.width / 2, y: this.props.height / 2 };
    const rand = Math.random();
    
    if(!this.nodes) {
      this.nodes = this.createNodes(data);
    }

    if(!this.simulation){
      this.simulation = d3.forceSimulation()
        //.velocityDecay(0.5)
        .force('x', d3.forceX().strength(this.forceStrength).x(d => centered ? center.x : d.xPos))
        .force('y', d3.forceY().strength(centered ? 0.07 : 0.01).y(center.y))
        .force('charge', d3.forceManyBody().strength(d => this.charge(d)))
    }

    if(this.state && (!this.state.data || !data)) {
      this.nodes = this.createNodes(data);
      this.simulation
      .nodes(this.nodes)
      .on('tick', () => this.ticked());
    }

    this.chargePow = centered ? 2.05 : 1.8;
    this.simulation
      .force('x', d3.forceX().strength(this.forceStrength).x(d => centered ? center.x : d.xPos))
      .force('y', d3.forceY().strength(centered ? 0.07 : 0.02).y(center.y))
      .force('charge', d3.forceManyBody().strength(d => this.charge(d)))
      .alphaTarget(0.3)
      .restart()

    this.bubbles = this.bubbleLayer.selectAll('.bubble')
      .data(this.nodes, d => d.id);

    this.bubbles.exit()
      .transition()
      .duration(1000)
      .style('opacity', 0)
      .remove();

    var bubblesEnter = this.bubbles.enter()
      .append('svg:g')
      .attr('class', d => `bubble bubble-${d.id}`)

    bubblesEnter
      .append('defs')
      .append('pattern')
      .attr('id', d => `image-${d.id.replace(' ', '_')}-${rand}`)
      .attr('width', 1)
      .attr('height', 1)
      .append('svg:image')
      .attr('xlink:href', d => {
        if(d.img) {
          try {
            const img = require(`../../../assets/images/contries/${d.img}.png`);
            return img;
          } catch (ex) {
            return '';
          }
        }
        return '';
      })
      .attr('width', d => 2 * d.radius)
      .attr('height', d => 2 * d.radius)
      .style('opacity', 1);

    bubblesEnter.append('circle')
      .attr('class', d => `circleBubble ${d.id}`)
      .attr('r', d => d.radius)
      .style('stroke-width', 2)
      .style('cursor', 'pointer')
      .style('opacity', 1)

    bubblesEnter.append('text')
      .classed('cityLabel', true)
      .attr('x', 0)
      .attr('y', 0)
      .attr('font-size', 10)
      .attr('dominant-baseline', 'middle')
      .attr('text-anchor', 'middle')
      .attr('textShadow', 'none')
      .attr('stroke-width', 0)
      .attr('fill', 'black')
      .attr('opacity', state.step >= 5 ? 0 : 1)
      .text(d => d.value)

    bubblesEnter
      .on('mouseenter', function(d) {
        const elem = d3.select(this).select('circle');
        elem
          .style('stroke', '#3a3a48')
          .style('stroke-width', 2.5);
        self.tooltip		
          .style('opacity', 1);		
        
        self.tooltip.html(`
          <h3>${d.value} </h3>
          Population: ${d3.format(',')(d.count)} <br/>
          Migrant proportion: ${d.foreignBornRatio}% <br/>
        `);
      })
      .on('mousemove', function(d) { 
        self.tooltip
          .style('left', (d3.event.pageX + 10) + 'px')
          .style('top', (d3.event.pageY +10) + 'px');
      })
      .on('mouseleave', function(d) { 
        d3.select(this)
          .select('circle')
          .style('fill', d => {
            if (d.img && state.showCountry) {
              return `url(#image-${d.id.replace(' ', '_')}-${rand})`;
            } else if (d.id === state.highlight) {
              return '#e91e63';
            } else {
              return d.color;
            }
          })
          .style('stroke', d => {
            if (d.id === state.highlight) {
              return 'black';
            } else {
              return d3.rgb(d.color).darker();
            }
          })
          .style('stroke-width', 2);
        self.tooltip	
          .style('opacity', 0);
      })

    this.bubbles = this.bubbles
      .merge(bubblesEnter)
      .style('fill', d => {
        if(state.step >= 5){
          return 'none';
        } else if (d.img && state.showCountry) {
          return `url(#image-${d.id.replace(' ', '_')}-${rand})`;
        } else if (d.id === state.highlight) {
          return '#e91e63';
        } else {
          return d.color;
        }
      })
      .style('stroke', d => {
        if (d.id === state.highlight) {
          return 'black';
        } else {
          return d3.rgb(d.color).darker();
        }
      })

    this.bubbles.transition()
      .duration(300)
      .attr('transform', d => `translate(${d.x}, ${d.y})`)
      .style('fill', d => d.color)
      .style('stroke', d => d3.rgb(d.color).darker())
      .style('fill', d => {
        if(state.step >= 5){
          return 'none';
        } else if (d.img && state.showCountry) {
          return `url(#image-${d.id.replace(' ', '_')}-${rand})`;
        } else if (d.id === state.highlight) {
          return '#e91e63';
        } else {
          return d.color;
        }
      })
      .style('stroke', d => {
        if (d.id === state.highlight) {
          return 'black';
        } else {
          return d3.rgb(d.color).darker();
        }
      })
      .style('opacity', 1)
      //.attr('r', function (d) { return d.radius; })

    this.bubbles.select('.cityLabel')
      .transition()
      .duration(300)
      .attr('opacity', state.step >= 5 ? 0 : 1)
  }

  createNodes(data) {
    if(data) {
      const size = Math.min(this.props.width, this.props.height);
      const totalCount = data.reduce((sum, d) => sum + d.count, 0);
      const radiusScale = d3.scalePow()
        .exponent(0.5)
        .range([1, size * (0.5 + 0.2)])
        .domain([0, totalCount * Math.PI]);

      const xScale = d3.scaleLinear()
        .range([0, this.props.width])
        .domain([0, 100])

      const nodes = data.map((d) => {
        return {
          ...d,
          radius: radiusScale(d.count),
          count: +d.count,
          value: d.value !== null ? d.value : 'Unknown',
          color: d.color ? d.color : '#ececec',
          proportion: d.count/totalCount,
          xPos: xScale(d.foreignBornRatio),
          x: Math.random() * this.props.width,
          y: Math.random() * this.props.height
        };
      });

      // sort them to prevent occlusion of smaller nodes.
      nodes.sort((a, b) => b.count - a.count);
      return nodes;
    } else {
      return  [];
    }
  }

  drawAxis(state) {
    const xScale = d3.scaleLinear()
      .range([0, this.props.width])
      .domain([0, 100])

    this.xAxis = d3.axisTop(xScale)
      .tickSize(-this.props.height)
      .tickFormat(d => `${d}%`)

    const self = this;
    this.axis
      .style('opacity', 0)
      .call(function(g){ self.customXAxis(g, self) });

    if(!this.legendXAxis) {
      this.legendXAxis = this.axis.append('text')             
        .attr('transform', `translate(${this.props.width + 10}, ${-20})`)
        .style('text-anchor', 'end')
        .attr('fill', 'grey')
        .attr('stroke-width', 0)
        .attr('font-size', 12)
        .text(this.intl.formatMessage({ id: 'app.bubbleLegend' }))
    }
  }

  customXAxis(g, self) {
    g.transition()
      .duration(2000)
      .style('opacity', self.centered || self.zoomed ? 0 : 1)
    g.call(self.xAxis);
    g.select('.domain').remove();
    g.selectAll('.tick line')
      .attr('stroke', '#D0D0D0')
      .attr('stroke-dasharray', '2,2')
    //g.selectAll('.tick text').attr('x', 4).attr('dy', -4);
  }

  charge(d) {
    return -(d.radius ** this.chargePow) * this.forceStrength;
  }

  ticked() {
    this.bubbles
      .attr('transform', d => `translate(${d.x}, ${d.y})`)
      //.attr('cx', d => d.x)
      //.attr('cy', d => d.y);
  }
}


